import React, { useEffect, useState } from "react";
import ReactDOMServer from "react-dom/server";
import juice from "juice";
import { ContentState, EditorState } from "draft-js";
import { convertToHTML } from "draft-convert";
import { TEmailCompositionForm } from "../../../../../../domain/codecs/form/EmailCompositionForm";
import { onChangeForm } from "../../../../../../shared/src/codecs/types/form";
import { isFilled, requireExhaustive } from "../../../../../../shared/src/util";
import { Relative } from "../../../BuildingBlocks/Relative";
import { TAttachmentType } from "../CRMEmailAttachmentFilterSearchSelect/CRMEmailAttachmentFilterSearchSelect";
import { getAttachedEnquiriesInDetail, getAttachedFileInDetail, getAttachedQuotesInDetail, getAttachedRedemptionStatementsInDetail, getAttachedSizedFiles } from "../CRMComposeEmailAttachments/CRMComposeEmailAttachments";
import { getAttachmentSizeSummary } from "../../../../functions/email/getAttachmentSizeSummary";
import { CRMLegalEmail } from "../CRMLegalEmail/CRMLegalEmail";
import { StaffUser } from "../../../../../../domain/codecs/User";
import { anyMatchPredicate, contains } from "../../../../../../shared/src/utilsByDomain/array";
import { CRMComposeEmailFormViewMain } from "../CRMComposeEmailFormViewMain/CRMComposeEmailFormViewMain";
import { CRMComposeEmailFormViewAttachedEmail } from "../CRMComposeEmailFormViewAttachedEmail/CRMComposeEmailFormViewAttachedEmail";
import { isAFunction } from "../../../../functions/functions";
import { doesEmailContain } from "../../../../functions/email/doesEmailContain";
import { isEmailViewType, TComposeEmailFormAttachmentOptionsProps, TComposeEmailFormCaseSearchProps, TComposeEmailFormControlProps, TComposeEmailFormWritingProps, TComposeEmailResolutionStatus, TContextType, TSendType, TEmailViewStatus } from "./CRMComposeEmailFormProps";
import { EmailAttachments, TEmailComposition } from "../../../../../../domain/codecs/EmailComposition";
import * as regexes from "./../../../../../../shared/src/regexes";
import { CRMBlockPopover } from "../../../CRM/CRMBlock/CRMBlockPopover/CRMBlockPopover";
import { CRMSpacing } from "../../../../models/CRMSpacing";
import { textToEditorState } from "../../../CRMRichTextArea/CRMRichTextAreaUtils";
import { CRMComposeEmailAttachmentSearchPopover } from "../CRMComposeEmailAttachmentSearchPopover/CRMComposeEmailAttachmentSearchPopover";
import { CRMComposeEmailTemplatesPopover } from "../CRMComposeEmailTemplatesPopover/CRMComposeEmailTemplatesPopover";
import { TEmailTemplatesForm } from "../../../../../../domain/codecs/form/EmailTemplatesForm";
import { TEmailTemplateRecord } from "../../../../../../domain/emailTemplates/templateLibrary";
import { resolveTemplateTokens } from "../../../../../../domain/emailTemplates/tokens";
import { pipe } from "fp-ts/lib/function";
import { CRMEmailQualityScoreModal, TQualityScoreData } from "../CRMEmailQualityScoreModal/CRMEmailQualityScoreModal";
import { useDictation } from "./useDictation";

type TComposeEmailFormProps = 
    TComposeEmailFormWritingProps &
    TComposeEmailFormCaseSearchProps &
    TComposeEmailFormAttachmentOptionsProps &
    TComposeEmailFormControlProps &
    {   
        caseEmailPageCaseId?: string;
        viewStatus?: TEmailViewStatus;
        apiUrl: string;
        templatesForm: TEmailTemplatesForm;
    }
;

export const CRMComposeEmailForm = (props: React.PropsWithChildren<TComposeEmailFormProps>): JSX.Element => {

    const [viewStatus, setViewStatus] = useState<TEmailViewStatus>("compose-case-new");
    const [resolutionStatus, setResolutionStatus] = useState<TComposeEmailResolutionStatus>("none");
    const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
    const [aiReviewStatus, setAiReviewStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
    const [aiReviewFeedback, setAiReviewFeedback] = useState("");
    const [aiReviewPreviousEditorState, setAiReviewPreviousEditorState] = useState<EditorState | null>(null);
    const [qualityScore, setQualityScore] = useState<TQualityScoreData | null>(null);
    // Sticky for the life of the compose: a dictated body that the sender then
    // edits by hand is still a dictated body, and the measurement we want is
    // "did the tidy produce this text", not "is it untouched".
    const [wasDictated, setWasDictated] = useState(false);
    const [qualityScoredBodyHash, setQualityScoredBodyHash] = useState<string | null>(null);
    const [qualityScoreStatus, setQualityScoreStatus] = useState<"idle" | "loading" | "modal" | "error">("idle");
    const [qualityRewriteStatus, setQualityRewriteStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
    const [pendingSendType, setPendingSendType] = useState<TSendType | null>(null);


    useEffect(
        () => setEditorState(textToEditorState(props.form.edited.body_draft)),
        []
    );

    const lastExternalBodyDraft = React.useRef(props.form.edited.body_draft);
    useEffect(
        () => {
            const prev = lastExternalBodyDraft.current;
            const next = props.form.edited.body_draft;
            if (next !== "" && next !== prev && prev === "") {
                setEditorState(textToEditorState(next));
            }
            lastExternalBodyDraft.current = next;
        },
        [props.form.edited.body_draft]
    );

    useEffect(
        () => {
            if (props.form.edited.body === "" && props.form.edited.body_draft === "") {
                setEditorState(EditorState.createEmpty());
            }
        },
        [props.form.edited.body]
    );
    
    useEffect(
        () => {
            if (props.form.status === "success") {
                setResolutionStatus("clear-email");
            }
        },
        [props.form.status],
    );

    useEffect(
        () => setViewStatus(inferComposeViewStatus()),
        [
            props.form.edited.bcc_addresses,
            props.form.edited.cc_addresses,
            props.form.edited.sentFrom,
            props.form.edited.sendTo,
            props.attachedEmail
        ],
    )

    useEffect(
        () => {
            if (isEmailViewType("confidential", viewStatus)) {
                props.onChange(
                    getFormWithUpdatedEdit({
                        caseID: null,
                        attachments: EmailAttachments.newDefault(),
                        attachment_delivery_mode: "link",
                    })
                );
            }
        },
        [viewStatus]
    )


    const getEmailSendType = (): TSendType => {
        if (isAnEmailBeingSentTo(props.attachedEmail.children.email.from_address)) {
            return "reply";
        }

        if (hasAttachedEmail()) {
            return "forward";
        }
        
        return "new";
    }

    const inferContextType = (): TContextType => {
        if (
            isEmailAddressConfidential(props.form.edited.sentFrom)
            || isAnyEmailAddressConfidential(props.form.edited.sendTo)
            || isAnyEmailAddressConfidential(props.form.edited.cc_addresses)
            || isAnyEmailAddressConfidential(props.form.edited.bcc_addresses)
        ) {
            return "confidential";
        }
        return "case";
    };

    const inferComposeViewStatus = (): TEmailViewStatus => `compose-${inferContextType()}-${getEmailSendType()}`;

    const getSelectedCase = () => props.form.children.cases_search_form.children.find((legalCase) => legalCase.id === props.form.edited.caseID);

    const clearEmailTextEditor = () => {
        setEditorState(
            EditorState.push(
                editorState, 
                ContentState.createFromText(''),
                "remove-range"
            )
        );
    }

    const getFormWithUpdatedEdit = (edited: Partial<TEmailComposition>): TEmailCompositionForm => ({
        ...props.form,
        edited: {
            ...props.form.edited,
            ...edited
        }
    });

    const resetResolutionSubmission = () => setResolutionStatus("none");

    const convertEmailTextAndAttachmentsToLegalEmailTemplateHTML = (): string => {
        const selectedCase = getSelectedCase();
        // this is anoying shuold be refined, its repeated code in email body
        const sentFromDetails = selectedCase?.case_handlers
            .filter((handler) => handler.id !== props.composingUser.id)
            .concat(props.composingUser)
            .find((handler) => handler.email === props.form.edited.sentFrom);

        const emailBodyAsHTML = convertToHTML(editorState.getCurrentContent()).toString();
        const finalEmailHTML = ReactDOMServer.renderToStaticMarkup(
            CRMLegalEmail({
                subject: props.form.edited.subject,
                body: emailBodyAsHTML,
                enquiries: getAttachedEnquiriesInDetail(props.form),
                files: getAttachedFileInDetail(props.form),
                redemptionStatements: getAttachedRedemptionStatementsInDetail(props.form),
                quotes: getAttachedQuotesInDetail(props.form),
                senderDetails: sentFromDetails || StaffUser.newDefault(),
                apiUrl: props.apiUrl,
                attachmentDeliveryMode: props.form.edited.attachment_delivery_mode,
            })
        );
        const finalEmailHTMLWithComments = finalEmailHTML.replace(/<(template) data-comment\b[^>]*>(.*?)<\/\1>/gm, '$2');

        return juice(
            finalEmailHTMLWithComments, 
            {
                'applyWidthAttributes': false,
                'applyHeightAttributes': false,
                'applyAttributesTableElements': false,
                'preserveImportant': true
            }
        );
    }

    const isCaseSelected = () => !!getSelectedCase();
    const isEmailAddressConfidential = doesEmailContain(regexes.constants.SAIL_LEGAL_CONFIDENTIAL_EMAIL);
    const isAnyEmailAddressConfidential = anyMatchPredicate<string>(isEmailAddressConfidential);
    const isAnEmailBeingSentTo = (email) => contains(email)(props.form.edited.sendTo);
    const hasAttachedEmail = () => !!props.attachedEmail?.children.email.id;
    const isEmailBodyValid = () => getEmailSendType() === "forward" ? true : isEmailBodyPopulated();
    const isEmailBodyPopulated = (): boolean => editorState.getCurrentContent().hasText();
    const isNotAnInvalidCaseEmail = () => isEmailViewType("confidential", viewStatus) ? true : isFilled(props.form.edited.caseID);

    const isWithinAttachmentBudget = (): boolean =>
        props.form.edited.attachment_delivery_mode === "link"
        || !getAttachmentSizeSummary(getAttachedSizedFiles(props.form)).isOverBudget;

    const canSubmit = (): boolean => (
        isNotAnInvalidCaseEmail()
        && isFilled(props.form.edited.sendTo)
        && isFilled(props.form.edited.sentFrom)
        && isFilled(props.form.edited.subject)
        && isEmailBodyValid()
        && isWithinAttachmentBudget()
    );

    const onChange = onChangeForm(props.form, props.onChange);

    const onChangeEmailContentState = (newEditorState: EditorState) => {
        setEditorState(newEditorState);
        const bodyDraftHtml = convertToHTML(newEditorState.getCurrentContent()).toString();
        lastExternalBodyDraft.current = bodyDraftHtml;
        onChange("body_draft")(bodyDraftHtml);
    }

    const appendDictationResult = (formattedBody: string) => {
        setWasDictated(true);
        const currentText = editorState.getCurrentContent().getPlainText("\n");
        const combined = currentText.trim() === ""
            ? formattedBody
            : `${currentText}\n\n${formattedBody}`;
        const newEditorState = EditorState.push(
            editorState,
            ContentState.createFromText(combined),
            "insert-characters"
        );
        onChangeEmailContentState(EditorState.moveFocusToEnd(newEditorState));
    };

    const dictationReplyingToPlainText = (props.attachedEmail?.children.email.body_html_or_text || "")
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();

    const dictation = useDictation({
        apiUrl: props.apiUrl,
        caseId: props.form.edited.caseID || null,
        sendTo: props.form.edited.sendTo || [],
        replyingToBody: dictationReplyingToPlainText,
        replyingToFrom: props.attachedEmail?.children.email.from_address || "",
        onResult: appendDictationResult,
    });

    const getTransactionType = () => getSelectedCase()?.transaction_type;
    const getSellerReasonForSale = () =>  getSelectedCase()?.seller_reason_for_sale;
    const getPurchasePaymentMethod = () => getSelectedCase()?.purchase_payment_method || "unknown";
    const getGopDocumentExists = () => getSelectedCase()?.gop_received === "grant_of_probate";
    const unregisteredPropertiesExist = () => pipe(
        getSelectedCase()?.properties
        .filter((property) => property.is_registered !== true).length || 0,
        (numberUnregistered) => numberUnregistered > 0
    );
    
    const onSelectEmailTemplate = (template: TEmailTemplateRecord) => {
        // Tokens are resolved at insert time, not at send time, so the handler can read and
        // correct the merged text - and so any [[marker]] left for a fact we do not hold is
        // visible to them while they are still editing.
        const resolvedContent = resolveTemplateTokens(
            template.body,
            props.form.children.email_template_context_form.status === "success"
                ? props.form.children.email_template_context_form.children
                : null,
        );
        let newEditorState = EditorState.createWithContent(ContentState.createFromText(resolvedContent))
        onChangeEmailContentState(newEditorState);
        setViewStatus(inferComposeViewStatus());
    }

    const onAddAttachment = (type: TAttachmentType, id: string) =>
        onChange("attachments")({
            ...props.form.edited.attachments,
            [type]: props.form.edited.attachments[type].concat(id)
        });
   
    const onRemoveAttachment = (type: TAttachmentType, id: string) =>
        onChange("attachments")({
            ...props.form.edited.attachments,
            [type]: props.form.edited.attachments[type].filter((attachmentId) => attachmentId !== id)
        });

    const onClose = () => {
        const emailBody = convertToHTML(editorState.getCurrentContent()).toString();
        const form = getFormWithUpdatedEdit({ body: emailBody });
        props.onChange(form);
        if (isAFunction<() => void>(props.onClose)) {
            props.onClose();
        }
    }

    const onClear = () => {
        resetResolutionSubmission();
        clearEmailTextEditor();
        props.onClear();
    }

    const onClickAiReview = () => {
        if (aiReviewStatus === "loading") {
            return;
        }
        if (!editorState.getCurrentContent().hasText()) {
            return;
        }

        setAiReviewStatus("loading");
        setAiReviewPreviousEditorState(editorState);

        const emailBodyPlainText = editorState.getCurrentContent().getPlainText("\n");

        const replyingToHtml = props.attachedEmail?.children.email.body_html_or_text || "";
        const replyingToPlainText = replyingToHtml.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

        fetch(`${props.apiUrl}/v1/email/ai-clear-review`, {
            method: "POST",
            credentials: "include",
            headers: { "content-type": "application/json", "accept": "application/json" },
            body: JSON.stringify({
                email_body: emailBodyPlainText,
                case_id: props.form.edited.caseID || null,
                send_to: props.form.edited.sendTo || [],
                replying_to_body: replyingToPlainText,
                replying_to_from: props.attachedEmail?.children.email.from_address || "",
            }),
        })
            .then((res) => res.json())
            .then((json: { data?: { improved_body: string; feedback: string } }) => {
                if (json.data?.improved_body && json.data?.feedback) {
                    const newEditorState = EditorState.createWithContent(
                        ContentState.createFromText(json.data.improved_body)
                    );
                    onChangeEmailContentState(newEditorState);
                    setAiReviewFeedback(json.data.feedback);
                    setAiReviewStatus("success");
                } else {
                    setAiReviewStatus("error");
                }
            })
            .catch(() => {
                setAiReviewStatus("error");
            });
    };

    const onRevertAiReview = () => {
        if (aiReviewPreviousEditorState) {
            onChangeEmailContentState(aiReviewPreviousEditorState);
            setAiReviewPreviousEditorState(null);
            setAiReviewStatus("idle");
            setAiReviewFeedback("");
        }
    };

    const hashBody = (s: string): string => {
        let h = 0;
        for (let i = 0; i < s.length; i++) {
            h = ((h << 5) - h) + s.charCodeAt(i);
            h |= 0;
        }
        return `${h}-${s.length}`;
    };

    const dispatchSend = (sendType: TSendType, form: TEmailCompositionForm) => {
        switch (sendType) {
            case "new":
                props.onSubmit(form);
                break;
            case "forward":
                props.onForward(form);
                break;
            case "reply":
                props.onReply(form);
                break;
            default:
                requireExhaustive(sendType);
        }
    };

    const buildScoringRequestBody = () => {
        const emailBodyPlainText = editorState.getCurrentContent().getPlainText("\n");
        const replyingToHtml = props.attachedEmail?.children.email.body_html_or_text || "";
        const replyingToPlainText = replyingToHtml.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
        const attachments = props.form.edited.attachments;
        const hasTemplateAttachments =
            attachments.enquiries.length > 0
            || attachments.redemption_statements.length > 0
            || attachments.quotes.length > 0;
        return {
            emailBodyPlainText,
            replyingToPlainText,
            requestBody: {
                email_body: emailBodyPlainText,
                subject: props.form.edited.subject,
                case_id: props.form.edited.caseID || null,
                send_to: props.form.edited.sendTo || [],
                cc_addresses: props.form.edited.cc_addresses || [],
                bcc_addresses: props.form.edited.bcc_addresses || [],
                has_template_attachments: hasTemplateAttachments,
                replying_to_body: replyingToPlainText,
                replying_to_from: props.attachedEmail?.children.email.from_address || "",
            },
        };
    };

    const sendWithScore = (sendType: TSendType, score: TQualityScoreData | null, wasOverridden: boolean) => {
        const form = getFormWithUpdatedEdit({
            body: convertEmailTextAndAttachmentsToLegalEmailTemplateHTML(),
            quality_score_json: score === null ? "" : JSON.stringify(score),
            was_quality_overridden: wasOverridden,
            was_dictated: wasDictated,
        });
        dispatchSend(sendType, form);
    };

    const onSend = () => {
        const sendType = getEmailSendType();
        const { emailBodyPlainText, requestBody } = buildScoringRequestBody();
        const bodyHash = hashBody(emailBodyPlainText);

        if (qualityScore !== null && qualityScoredBodyHash === bodyHash) {
            if (qualityScore.verdict === "send_confidently" || qualityScore.verdict === null) {
                sendWithScore(sendType, qualityScore, false);
                return;
            }
            setPendingSendType(sendType);
            setQualityScoreStatus("modal");
            return;
        }

        setQualityScoreStatus("loading");
        fetch(`${props.apiUrl}/v1/email/ai-quality-score`, {
            method: "POST",
            credentials: "include",
            headers: { "content-type": "application/json", "accept": "application/json" },
            body: JSON.stringify(requestBody),
        })
            .then((res) => res.json())
            .then((json: { data?: TQualityScoreData }) => {
                const score = json.data;
                if (!score) {
                    sendWithScore(sendType, null, false);
                    setQualityScoreStatus("idle");
                    return;
                }
                setQualityScore(score);
                setQualityScoredBodyHash(bodyHash);
                if (score.verdict === "send_confidently" || score.verdict === null) {
                    sendWithScore(sendType, score, false);
                    setQualityScoreStatus("idle");
                } else {
                    setPendingSendType(sendType);
                    setQualityScoreStatus("modal");
                }
            })
            .catch(() => {
                sendWithScore(sendType, null, false);
                setQualityScoreStatus("idle");
            });
    };

    const onQualityModalSendAnyway = () => {
        if (pendingSendType && qualityScore) {
            sendWithScore(pendingSendType, qualityScore, true);
        }
        setQualityScoreStatus("idle");
        setPendingSendType(null);
    };

    const onQualityModalLetMeEdit = () => {
        setQualityScoreStatus("idle");
        setPendingSendType(null);
    };

    const onQualityModalRunRewrite = () => {
        if (qualityRewriteStatus === "loading" || !qualityScore) return;
        setQualityRewriteStatus("loading");
        setAiReviewPreviousEditorState(editorState);

        const emailBodyPlainText = editorState.getCurrentContent().getPlainText("\n");
        const replyingToHtml = props.attachedEmail?.children.email.body_html_or_text || "";
        const replyingToPlainText = replyingToHtml.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
        const additionalFocus = qualityScore.suggestions
            .map((s) => `- ${s.dimension}: ${s.issue} → ${s.suggestion}`)
            .join("\n");

        fetch(`${props.apiUrl}/v1/email/ai-clear-review`, {
            method: "POST",
            credentials: "include",
            headers: { "content-type": "application/json", "accept": "application/json" },
            body: JSON.stringify({
                email_body: emailBodyPlainText,
                case_id: props.form.edited.caseID || null,
                send_to: props.form.edited.sendTo || [],
                replying_to_body: replyingToPlainText,
                replying_to_from: props.attachedEmail?.children.email.from_address || "",
                additional_focus: additionalFocus,
            }),
        })
            .then((res) => res.json())
            .then((json: { data?: { improved_body: string; feedback: string } }) => {
                if (json.data?.improved_body) {
                    const newEditorState = EditorState.createWithContent(
                        ContentState.createFromText(json.data.improved_body)
                    );
                    onChangeEmailContentState(newEditorState);
                    setAiReviewFeedback(json.data.feedback || "");
                    setAiReviewStatus("success");
                    setQualityRewriteStatus("success");
                    setQualityScore(null);
                    setQualityScoredBodyHash(null);
                    setQualityScoreStatus("idle");
                    setPendingSendType(null);
                } else {
                    setQualityRewriteStatus("error");
                }
            })
            .catch(() => {
                setQualityRewriteStatus("error");
            });
    };
    
    
    return (
        <Relative>

            {/* [MODAL] - EMAIL QUALITY SCORE */}
            {qualityScoreStatus === "modal" && qualityScore !== null &&
                <CRMEmailQualityScoreModal
                    score={qualityScore}
                    onSendAnyway={onQualityModalSendAnyway}
                    onLetMeEdit={onQualityModalLetMeEdit}
                    onRunAiRewrite={onQualityModalRunRewrite}
                    rewriteStatus={qualityRewriteStatus}
                />
            }

            {/* [POPOVER] - SELECT EMAIL TEMPLATE */}
            {viewStatus === "view-email-templates" &&
                <CRMComposeEmailTemplatesPopover
                    transactionType={getTransactionType() || "unknown"}
                    sellerReasonForSale={getSellerReasonForSale() || "unknown"}
                    gopDocumentExists={getGopDocumentExists()}
                    purchasePaymentMethod={getPurchasePaymentMethod()}
                    isUnregistered={unregisteredPropertiesExist()}
                    templatesForm={props.templatesForm}
                    onClose={() => setViewStatus(inferComposeViewStatus())}
                    onSelectTemplate={onSelectEmailTemplate}
                />
            }
            
            {/* [POPOVER] - SELECT ATTACHMENT */}
            {viewStatus === "view-attachment-search" &&
                <CRMComposeEmailAttachmentSearchPopover
                    form={props.form}
                    onClose={() => setViewStatus(inferComposeViewStatus())}
                    onAddAttachment={onAddAttachment}
                />
            }

            {/* [POPOVER] - VIEW ATTACHED EMAIL */}
            {viewStatus === "view-attached-email" &&
                <CRMBlockPopover
                    padding={CRMSpacing.MEDIUM}
                    top={CRMSpacing.TINY}
                    height="720px" 
                    maxHeight="720px"
                >
                    <CRMComposeEmailFormViewAttachedEmail
                        viewStatus={inferComposeViewStatus()}
                        attachedEmail={props.attachedEmail}
                        onClick={() => setViewStatus(inferComposeViewStatus())}
                    />
                </CRMBlockPopover>
            }
            
            {/* [BODY] - EMAIL FORM */}
            <CRMComposeEmailFormViewMain
                {...props}

                viewStatus={viewStatus}

                isACaseSelected={isCaseSelected()}
                isSubmittable={canSubmit()}
                isSendInProgress={qualityScoreStatus === "loading"}

                resolutionStatus={resolutionStatus}
                onResetResolutionSubmission={resetResolutionSubmission}

                emailContentState={editorState}
                onChangeEmailContentState={onChangeEmailContentState}

                onSend={onSend}
                onClose={onClose}
                onClear={onClear}
                onOpenAttachments={() => setViewStatus("view-attachment-search")}
                onRemoveAttachment={onRemoveAttachment}
                onChangeAttachmentDeliveryMode={onChange("attachment_delivery_mode")}
                onOpenAttachedEmail={() => setViewStatus("view-attached-email")}
                onOpenEmailTemplates={() => setViewStatus("view-email-templates")}

                aiReviewStatus={aiReviewStatus}
                aiReviewFeedback={aiReviewFeedback}
                aiReviewHasRevert={aiReviewPreviousEditorState !== null}
                onClickAiReview={onClickAiReview}
                onRevertAiReview={onRevertAiReview}

                dictationStatus={dictation.status}
                dictationMessage={dictation.message}
                dictationCanRetry={dictation.canRetry}
                dictationElapsedSeconds={dictation.elapsedSeconds}
                dictationProcessingSeconds={dictation.processingSeconds}
                onClickDictate={dictation.start}
                onStopDictate={dictation.stop}
                onRetryDictate={dictation.retry}
            />
        </Relative>
    );
};
